import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @fileoverview CountBadge Component
 *
 * Badge component for displaying numeric counts with overflow handling.
 * Automatically shows "99+" when count exceeds the maximum threshold.
 *
 * @author LOCI Development Team
 * @since 1.1.0
 */
import { forwardRef } from 'react';
import { useTheme } from '../../theme';
/**
 * Size configurations for CountBadge
 * Note: CountBadge only supports sm and md sizes as per requirements
 */
const SIZE_CONFIG = {
    sm: {
        height: 'h-5',
        minWidth: 'min-w-[20px]',
        padding: 'px-1.5',
        fontSize: 'text-xs',
    },
    md: {
        height: 'h-6',
        minWidth: 'min-w-[24px]',
        padding: 'px-2',
        fontSize: 'text-sm',
    },
};
/**
 * CountBadge Component
 *
 * Displays numeric counts with automatic overflow handling and consistent sizing.
 */
export const CountBadge = forwardRef(({ count, max = 99, size = 'md', variant = 'filled', className = '', 'data-testid': testId, 'aria-label': ariaLabel, }, ref) => {
    const theme = useTheme();
    // Clamp count to minimum of 0 and handle overflow
    const clampedCount = Math.max(0, count);
    const displayCount = clampedCount > max ? `${max}+` : clampedCount.toString();
    // Fallback to 'md' for unsupported sizes
    const sizeConfig = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
    const classes = [
        'inline-flex items-center justify-center',
        'font-semibold rounded-full',
        'border transition-colors duration-200',
        sizeConfig.height,
        sizeConfig.minWidth,
        sizeConfig.padding,
        sizeConfig.fontSize,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const styles = {
        backgroundColor: theme.colors.error,
        color: 'white',
        borderColor: theme.colors.error,
    };
    return (_jsx("span", { ref: ref, className: classes, style: styles, "data-testid": testId || `count-badge-${count}`, role: "status", "aria-label": ariaLabel || `Count: ${displayCount}`, "aria-live": "polite", children: _jsx("span", { className: "whitespace-nowrap select-none tabular-nums", children: displayCount }) }));
});
CountBadge.displayName = 'CountBadge';
